import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [activeTab, setActiveTab] = useState<'privacy' | 'terms'>('privacy');
  const [isScrolled, setIsScrolled] = useState(false);
  const [contactFormStatus, setContactFormStatus] = useState('');
  const [newsletterFormStatus, setNewsletterFormStatus] = useState('');

  // Handle scroll for navbar
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  // Handle contact form submission
  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setContactFormStatus('Message must be 500 characters or less');
      return;
    }
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5ds7qefmrgp9cvlvku0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        setContactFormStatus('Message sent successfully!');
        form.reset();
        setTimeout(() => setContactFormStatus(''), 3000);
      } else {
        setContactFormStatus('Failed to send message. Please try again.');
      }
    } catch (error) {
      setContactFormStatus('An error occurred. Please try again.');
    }
  };

  // Handle newsletter form submission
  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5ds7qefmrgp9cvlvktg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        setNewsletterFormStatus('Subscribed successfully!');
        form.reset();
        setTimeout(() => setNewsletterFormStatus(''), 3000);
      } else {
        setNewsletterFormStatus('Failed to subscribe.');
      }
    } catch (error) {
      setNewsletterFormStatus('An error occurred.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/0f59412c-d85e-4a07-b2f0-2bfe0e6bd8a8.png" alt="TOBRAL Logo" className="w-10 h-10 object-contain" />
            <span className="text-xl font-bold text-gray-900">TOBRAL</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#home" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Home</a>
            <a href="#about" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">About</a>
            <a href="#vpn-service" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">VPN Service</a>
            <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Contact</a>
          </div>
          <a href="#contact" className="hidden md:block bg-black text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-purple-50 to-blue-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-transparent"></div>
        <img 
          src="https://public.readdy.ai/ai/img_res/b9ca0be530eba2cc89a764ab5a6a7064.jpg" 
          alt="VPN Security Background" 
          className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
        />
        
        <div className="relative max-w-7xl mx-auto px-6 pt-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="text-xs font-semibold text-gray-600 tracking-wide">SECURE VPN SERVICE</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Browse Safely<br />
                Stay Anonymous<br />
                <span className="text-teal-600">Protect Your Privacy</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                Experience ultimate online freedom with military-grade encryption. TOBRAL VPN keeps your data secure and your identity anonymous across all your devices.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className="bg-black text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center">
                  Start Protecting Now <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
              <p className="text-sm text-gray-500">
                <i className="ri-shield-check-line text-teal-600 mr-2"></i>
                Trusted by users worldwide for secure browsing
              </p>
            </div>
            <div className="relative h-96 md:h-[500px]">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%203D%20illustration%20of%20digital%20shield%20protecting%20encrypted%20data%20with%20glowing%20lock%20symbols%20and%20secure%20connection%20pathways%20in%20vibrant%20teal%20and%20purple%20gradients%20featuring%20floating%20geometric%20elements%20and%20network%20nodes%20representing%20VPN%20security%20technology%20in%20a%20clean%20minimalist%20style%20with%20soft%20lighting&width=800&height=1000&seq=hero-visual-001&orientation=portrait" 
                alt="VPN Protection Illustration" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-block bg-gray-100 px-4 py-2 rounded-full">
                <span className="text-xs font-semibold text-gray-600 tracking-wide">ABOUT TOBRAL</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Dedicated to Your<br />
                Digital Privacy<br />
                <span className="text-teal-600">Since Day One</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  TOBRAL d.o.o. is a Croatian technology company committed to protecting your online privacy and security. Based in Đakovo, we develop cutting-edge applications designed to give you control over your digital life.
                </p>
                <p>
                  Our VPN service is built on the foundation of trust, transparency, and uncompromising security. We believe that privacy is a fundamental right, and we're dedicated to making it accessible to everyone through innovative technology solutions.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 shadow-2xl">
                <div className="space-y-6">
                  <div className="text-white/60 text-sm font-semibold tracking-wide">OUR MISSION</div>
                  <p className="text-white text-xl leading-relaxed">
                    To empower individuals and businesses with secure, reliable, and user-friendly privacy solutions that protect their digital freedom without compromise.
                  </p>
                  <div className="pt-6 border-t border-white/10 flex items-center gap-3 text-white/80">
                    <i className="ri-map-pin-line text-teal-400"></i>
                    <span className="text-sm">Based in Đakovo, Croatia</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Service Features */}
      <section id="vpn-service" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose TOBRAL VPN <i className="ri-shield-check-line text-teal-600"></i>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the perfect combination of security, speed, and simplicity
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group relative bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-50"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-lock-line text-4xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Military-Grade Encryption</h3>
                <p className="text-white/90 leading-relaxed">
                  Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide to safeguard classified information.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group relative bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-50"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-global-line text-4xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Global Server Network</h3>
                <p className="text-white/90 leading-relaxed">
                  Connect to high-speed servers around the world for optimal performance. Access content from anywhere while maintaining lightning-fast connection speeds.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group relative bg-gradient-to-br from-teal-600 to-teal-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-50"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-eye-off-line text-4xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">No-Log Policy</h3>
                <p className="text-white/90 leading-relaxed">
                  We don't track, collect, or store your online activity. Your privacy is guaranteed with our strict zero-logging policy verified by independent audits.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy First Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-700 to-emerald-900">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="inline-block bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <span className="text-xs font-semibold text-white/90 tracking-wide">PRIVACY FIRST</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-16">Your Data Stays Yours</h2>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="space-y-4">
              <div className="w-16 h-16 bg-emerald-500/30 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-database-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white">Zero Data Collection</h3>
              <p className="text-white/80 leading-relaxed">
                We don't collect, store, or share any of your personal information or browsing history.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="w-16 h-16 bg-emerald-500/30 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-user-forbid-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white">Anonymous Browsing</h3>
              <p className="text-white/80 leading-relaxed">
                Your IP address is masked, making your online activities completely anonymous and untraceable.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="w-16 h-16 bg-emerald-500/30 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-shield-keyhole-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white">Secure Protocols</h3>
              <p className="text-white/80 leading-relaxed">
                Advanced protocols like OpenVPN and WireGuard ensure maximum security and performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div className="space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Get In Touch</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Have questions about our VPN service? We're here to help. Reach out to us through any of the channels below.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-500 mb-1">Email</div>
                    <a href="mailto:contact@tobraldoo.com" className="text-lg text-gray-900 hover:text-teal-600 transition-colors cursor-pointer">
                      contact@tobraldoo.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-500 mb-1">Phone</div>
                    <a href="tel:+12406249463" className="text-lg text-gray-900 hover:text-teal-600 transition-colors cursor-pointer">
                      +1 240-624-9463
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-500 mb-1">Address</div>
                    <div className="text-lg text-gray-900">
                      Bana Jelačića 65<br />
                      31400 Đakovo<br />
                      Croatia
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleContactSubmit}>
                <div>
                  <input 
                    type="text" 
                    name="name"
                    placeholder="Your Name" 
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none text-base transition-colors"
                    required
                  />
                </div>
                
                <div>
                  <input 
                    type="email" 
                    name="email"
                    placeholder="Your Email" 
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none text-base transition-colors"
                    required
                  />
                </div>
                
                <div>
                  <input 
                    type="text" 
                    name="subject"
                    placeholder="Subject" 
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none text-base transition-colors"
                    required
                  />
                </div>
                
                <div>
                  <textarea 
                    name="message"
                    placeholder="Your Message" 
                    rows={5}
                    maxLength={500}
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none text-base transition-colors resize-none"
                    required
                  ></textarea>
                  <div className="text-xs text-gray-400 mt-1 text-right">Maximum 500 characters</div>
                </div>
                
                {contactFormStatus && (
                  <div className={`text-sm text-center ${contactFormStatus.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                    {contactFormStatus}
                  </div>
                )}
                
                <button 
                  type="submit" 
                  className="w-full bg-black text-white py-4 rounded-full text-base font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Message <i className="ri-send-plane-line ml-2"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy & Terms Section */}
      <section id="legal" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex justify-center gap-8 mb-12 border-b border-gray-200">
            <button 
              onClick={() => setActiveTab('privacy')}
              className={`pb-4 px-6 text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'privacy' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => setActiveTab('terms')}
              className={`pb-4 px-6 text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'terms' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Terms of Service
            </button>
          </div>

          {activeTab === 'privacy' && (
            <div className="space-y-8">
              <div>
                <p className="text-sm text-gray-500 mb-6">Last updated: January 2025</p>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h2>
                <p className="text-gray-600 leading-relaxed">
                  At TOBRAL d.o.o., we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your information when you use our VPN service.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Information We Collect</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  We are committed to a strict no-logging policy. We do not collect, store, or monitor:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                    <span>Your browsing history or online activities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                    <span>Connection timestamps or session duration</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                    <span>IP addresses or DNS queries</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                    <span>Bandwidth usage or traffic data</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Data Security</h3>
                <p className="text-gray-600 leading-relaxed">
                  We implement industry-standard security measures including AES-256 encryption to protect your data. All connections are secured using advanced protocols such as OpenVPN and WireGuard.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Third-Party Services</h3>
                <p className="text-gray-600 leading-relaxed">
                  We do not share, sell, or rent your personal information to third parties. Our VPN service operates independently without tracking or analytics tools that could compromise your privacy.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Your Rights</h3>
                <p className="text-gray-600 leading-relaxed">
                  You have the right to access, modify, or delete any personal information we may hold. Since we maintain a no-logging policy, there is minimal data to access or delete.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Contact Us</h3>
                <p className="text-gray-600 leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us at contact@tobraldoo.com
                </p>
              </div>
            </div>
          )}

          {activeTab === 'terms' && (
            <div className="space-y-8">
              <div>
                <p className="text-sm text-gray-500 mb-6">Last updated: January 2025</p>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Terms of Service</h2>
                <p className="text-gray-600 leading-relaxed">
                  By using TOBRAL VPN services, you agree to comply with and be bound by the following terms and conditions.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Acceptable Use</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  You agree to use our VPN service only for lawful purposes. Prohibited activities include:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-500 mt-1"></i>
                    <span>Engaging in illegal activities or violating any laws</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-500 mt-1"></i>
                    <span>Distributing malware, viruses, or harmful software</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-500 mt-1"></i>
                    <span>Attempting to breach or compromise our security systems</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-500 mt-1"></i>
                    <span>Harassing, threatening, or harming others</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Service Availability</h3>
                <p className="text-gray-600 leading-relaxed">
                  While we strive to provide uninterrupted service, we do not guarantee 100% uptime. Maintenance, updates, or unforeseen circumstances may temporarily affect service availability.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Account Responsibility</h3>
                <p className="text-gray-600 leading-relaxed">
                  You are responsible for maintaining the confidentiality of your account credentials. Any activities conducted through your account are your responsibility.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Limitation of Liability</h3>
                <p className="text-gray-600 leading-relaxed">
                  TOBRAL d.o.o. shall not be liable for any indirect, incidental, or consequential damages arising from the use or inability to use our VPN service.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Termination</h3>
                <p className="text-gray-600 leading-relaxed">
                  We reserve the right to suspend or terminate your access to our service if you violate these terms or engage in activities that harm our service or other users.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Changes to Terms</h3>
                <p className="text-gray-600 leading-relaxed">
                  We may update these Terms of Service from time to time. Continued use of our service after changes constitutes acceptance of the updated terms.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Contact Information</h3>
                <p className="text-gray-600 leading-relaxed">
                  For questions regarding these Terms of Service, please contact us at contact@tobraldoo.com
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/0f59412c-d85e-4a07-b2f0-2bfe0e6bd8a8.png" alt="TOBRAL Logo" className="w-10 h-10 object-contain" />
                <span className="text-xl font-bold">TOBRAL</span>
              </div>
              <p className="text-gray-400 leading-relaxed text-sm">
                Protecting your digital privacy with cutting-edge VPN technology. Browse safely, stay anonymous, and take control of your online freedom.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">Product</h4>
              <ul className="space-y-3">
                <li><a href="#vpn-service" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">VPN Service</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Features</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Get Started</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">Company</h4>
              <ul className="space-y-3">
                <li><a href="#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">About Us</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Contact</a></li>
                <li><button onClick={() => setActiveTab('privacy')} className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm text-left">Privacy Policy</button></li>
                <li><button onClick={() => setActiveTab('terms')} className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm text-left">Terms of Service</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">Connect</h4>
              <p className="text-gray-400 text-sm mb-4">Stay updated with our latest news</p>
              <form className="flex gap-2" data-readdy-form id="newsletter-form" onSubmit={handleNewsletterSubmit}>
                <input 
                  type="email" 
                  name="email"
                  placeholder="Your email" 
                  className="flex-1 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-teal-500 text-sm"
                  required
                />
                <button 
                  type="submit" 
                  className="bg-teal-600 text-white px-4 py-2 rounded-full hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap w-10 h-10 flex items-center justify-center"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
              {newsletterFormStatus && (
                <div className={`text-xs mt-2 ${newsletterFormStatus.includes('success') ? 'text-green-400' : 'text-red-400'}`}>
                  {newsletterFormStatus}
                </div>
              )}
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8">
            <div className="text-center">
              <div className="text-6xl md:text-8xl font-bold text-amber-100/10 mb-4 tracking-wider">TOBRAL</div>
              <p className="text-gray-400 text-sm">
                © 2025 TOBRAL d.o.o. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
